import { useEffect, useState, useCallback, useMemo } from "react";
import { useParams, useNavigate } from "react-router-dom";
import {
  Page,
  Layout,
  Card,
  Text,
  BlockStack,
  InlineStack,
  Badge,
  Banner,
  Toast,
  TextField,
  Select,
  Checkbox,
  Button,
  Spinner,
  DataTable,
  Filters,
} from "@shopify/polaris";
import { useVendors, type Vendor } from "../hooks/useVendors";
import {
  useVendorProducts,
  type VendorProductMerged,
} from "../hooks/useVendorProducts";
import DeleteConfirmationModal from "../components/DeleteConfirmationModal";
import {
  calculateNetCaseCost,
  calculateUnitCost,
  calculateMargin,
  formatCurrency,
  formatPercent,
  type CostInputs,
} from "../utils/cost-calculations";

const CATEGORIES = [
  { label: "Select...", value: "" },
  { label: "Dry", value: "Dry" },
  { label: "Canned", value: "Canned" },
  { label: "Treats", value: "Treats" },
  { label: "Food Refrigerated", value: "Food Refrigerated" },
  { label: "Food Frozen", value: "Food Frozen" },
  { label: "Litter", value: "Litter" },
  { label: "All", value: "All" },
];

interface EditableRow {
  shopifyVariantId: string;
  shopifyProductId: string;
  category: string;
  packSize: string;
  caseCost: string;
  offInvoice: string;
  byCheque: string;
  freeGoods: string;
  vendorSku: string;
  isDiscontinued: boolean;
  flaggedForReview: boolean;
  reviewNote: string;
  dirty: boolean;
}

export default function VendorDetail() {
  const { vendorId } = useParams<{ vendorId: string }>();
  const navigate = useNavigate();

  const { loadVendor, deleteVendor } = useVendors();
  const {
    products,
    loading: productsLoading,
    error: productsError,
    loadProducts,
    saveProducts,
    refreshFromShopify,
  } = useVendorProducts();

  const [vendor, setVendor] = useState<Vendor | null>(null);
  const [edits, setEdits] = useState<Map<string, EditableRow>>(new Map());
  const [saving, setSaving] = useState(false);
  const [syncing, setSyncing] = useState(false);
  const [toastMessage, setToastMessage] = useState<string | null>(null);
  const [deleteModalOpen, setDeleteModalOpen] = useState(false);
  const [searchValue, setSearchValue] = useState("");
  const [categoryFilter, setCategoryFilter] = useState("");
  const [vendorFilter, setVendorFilter] = useState("");
  const [typeFilter, setTypeFilter] = useState("");
  const [tagFilter, setTagFilter] = useState("");
  const [option1Filter, setOption1Filter] = useState("");
  const [option2Filter, setOption2Filter] = useState("");
  const [showFlaggedOnly, setShowFlaggedOnly] = useState(false);

  useEffect(() => {
    if (vendorId) {
      loadVendor(vendorId).then((v) => setVendor(v));
      loadProducts(vendorId);
    }
  }, [vendorId]);

  // Initialize editable state from products
  useEffect(() => {
    const map = new Map<string, EditableRow>();
    for (const p of products) {
      const db = p.dbRecord;
      map.set(p.shopifyVariantId, {
        shopifyVariantId: p.shopifyVariantId,
        shopifyProductId: p.shopifyProductId,
        category: db?.category || "",
        packSize: String(db?.packSize || 1),
        caseCost: db ? String(db.caseCost) : "",
        offInvoice: String(db?.offInvoice || 0),
        byCheque: String(db?.byCheque || 0),
        freeGoods: String(db?.freeGoods || 0),
        vendorSku: db?.vendorSku || "",
        isDiscontinued: db?.isDiscontinued || false,
        flaggedForReview: db?.flaggedForReview || false,
        reviewNote: db?.reviewNote || "",
        dirty: false,
      });
    }
    setEdits(map);
  }, [products]);

  const handleEditChange = useCallback(
    (variantId: string, field: keyof EditableRow, value: string | boolean) => {
      setEdits((prev) => {
        const next = new Map(prev);
        const row = { ...next.get(variantId)! };
        (row as any)[field] = value;
        row.dirty = true;
        next.set(variantId, row);
        return next;
      });
    },
    []
  );

  const getCostCalc = useCallback(
    (variantId: string, retailPrice: string) => {
      const row = edits.get(variantId);
      if (!row || !row.caseCost || parseFloat(row.caseCost) === 0) return null;

      const inputs: CostInputs = {
        caseCost: parseFloat(row.caseCost) || 0,
        vendorDiscountPct: vendor?.discountPercent || 0,
        offInvoice: parseFloat(row.offInvoice) || 0,
        byCheque: parseFloat(row.byCheque) || 0,
        freeGoods: parseFloat(row.freeGoods) || 0,
        packSize: parseInt(row.packSize) || 1,
        retailPrice: parseFloat(retailPrice) || 0,
      };

      return {
        netCaseCost: calculateNetCaseCost(inputs),
        unitCost: calculateUnitCost(inputs),
        margin: calculateMargin(inputs),
      };
    },
    [edits, vendor]
  );

  // Build dynamic filter options from product data
  const filterOptions = useMemo(() => {
    const vendors = new Set<string>();
    const types = new Set<string>();
    const tags = new Set<string>();
    const option1Values = new Set<string>();
    const option2Values = new Set<string>();
    let option1Name = "Option 1";
    let option2Name = "Option 2";

    for (const p of products) {
      if (p.productVendor) vendors.add(p.productVendor);
      if (p.productType) types.add(p.productType);
      if (p.tags) {
        for (const t of p.tags) tags.add(t);
      }
      if (p.selectedOptions) {
        for (const opt of p.selectedOptions) {
          if (opt.name && opt.value && opt.value !== "Default Title") {
            // Use the first two unique option names
            if (!option1Name || option1Name === "Option 1" || option1Name === opt.name) {
              option1Name = opt.name;
              option1Values.add(opt.value);
            } else if (!option2Name || option2Name === "Option 2" || option2Name === opt.name) {
              option2Name = opt.name;
              option2Values.add(opt.value);
            }
          }
        }
      }
    }

    const toSortedOptions = (s: Set<string>) =>
      Array.from(s).sort().map((v) => ({ label: v, value: v }));

    return {
      vendors: toSortedOptions(vendors),
      types: toSortedOptions(types),
      tags: toSortedOptions(tags),
      option1: { name: option1Name, values: toSortedOptions(option1Values) },
      option2: { name: option2Name, values: toSortedOptions(option2Values) },
    };
  }, [products]);

  const filteredProducts = useMemo(() => {
    return products.filter((p) => {
      if (searchValue) {
        const search = searchValue.toLowerCase();
        if (
          !p.productTitle.toLowerCase().includes(search) &&
          !p.sku.toLowerCase().includes(search) &&
          !(p.variantTitle || "").toLowerCase().includes(search)
        ) {
          return false;
        }
      }
      if (categoryFilter) {
        const row = edits.get(p.shopifyVariantId);
        if (row?.category !== categoryFilter) return false;
      }
      if (vendorFilter && p.productVendor !== vendorFilter) return false;
      if (typeFilter && p.productType !== typeFilter) return false;
      if (tagFilter && !(p.tags || []).includes(tagFilter)) return false;
      if (option1Filter) {
        const match = (p.selectedOptions || []).some(
          (o) => o.name === filterOptions.option1.name && o.value === option1Filter
        );
        if (!match) return false;
      }
      if (option2Filter) {
        const match = (p.selectedOptions || []).some(
          (o) => o.name === filterOptions.option2.name && o.value === option2Filter
        );
        if (!match) return false;
      }
      if (showFlaggedOnly) {
        const row = edits.get(p.shopifyVariantId);
        if (!row?.flaggedForReview) return false;
      }
      return true;
    });
  }, [products, searchValue, categoryFilter, vendorFilter, typeFilter, tagFilter, option1Filter, option2Filter, showFlaggedOnly, edits, filterOptions]);

  const dirtyCount = useMemo(
    () => Array.from(edits.values()).filter((r) => r.dirty).length,
    [edits]
  );

  const handleSaveAll = useCallback(async () => {
    if (!vendorId) return;
    setSaving(true);

    const dirtyRows = Array.from(edits.values()).filter(
      (r) => r.dirty && r.caseCost && parseFloat(r.caseCost) > 0
    );

    const items = dirtyRows.map((r) => ({
      shopifyVariantId: r.shopifyVariantId,
      shopifyProductId: r.shopifyProductId,
      category: r.category || undefined,
      packSize: parseInt(r.packSize) || 1,
      caseCost: parseFloat(r.caseCost),
      offInvoice: parseFloat(r.offInvoice) || 0,
      byCheque: parseFloat(r.byCheque) || 0,
      freeGoods: parseFloat(r.freeGoods) || 0,
      vendorSku: r.vendorSku || undefined,
      isDiscontinued: r.isDiscontinued,
      flaggedForReview: r.flaggedForReview,
      reviewNote: r.reviewNote || undefined,
    }));

    const success = await saveProducts(vendorId, items);
    if (success) {
      setToastMessage(`Saved ${items.length} product(s)`);
      await loadProducts(vendorId);
    }
    setSaving(false);
  }, [vendorId, edits, saveProducts, loadProducts]);

  const handleRefresh = useCallback(async () => {
    if (!vendorId) return;
    setSyncing(true);
    const success = await refreshFromShopify(vendorId);
    if (success) {
      await loadProducts(vendorId);
      setToastMessage("Refreshed from Shopify");
    }
    setSyncing(false);
  }, [vendorId, refreshFromShopify, loadProducts]);

  const handleDelete = useCallback(async () => {
    if (!vendorId) return;
    const success = await deleteVendor(vendorId);
    if (success) {
      navigate("/");
    }
    setDeleteModalOpen(false);
  }, [vendorId, deleteVendor, navigate]);

  if (!vendor) {
    return (
      <Page title="Loading...">
        <Layout>
          <Layout.Section>
            <Card>
              <Spinner size="large" />
            </Card>
          </Layout.Section>
        </Layout>
      </Page>
    );
  }

  const rows = filteredProducts.map((p) => {
    const row = edits.get(p.shopifyVariantId);
    const calc = getCostCalc(p.shopifyVariantId, p.price);

    return [
      p.sku || "\u2014",
      p.productTitle + (p.variantTitle && p.variantTitle !== p.productTitle ? ` - ${p.variantTitle}` : ""),
      String(p.qoh),
      <Select
        key={`cat-${p.shopifyVariantId}`}
        label=""
        labelHidden
        options={CATEGORIES}
        value={row?.category || ""}
        onChange={(v) => handleEditChange(p.shopifyVariantId, "category", v)}
      />,
      <TextField
        key={`ps-${p.shopifyVariantId}`}
        label=""
        labelHidden
        type="number"
        value={row?.packSize || "1"}
        onChange={(v) => handleEditChange(p.shopifyVariantId, "packSize", v)}
        autoComplete="off"
      />,
      <TextField
        key={`cc-${p.shopifyVariantId}`}
        label=""
        labelHidden
        type="number"
        value={row?.caseCost || ""}
        onChange={(v) => handleEditChange(p.shopifyVariantId, "caseCost", v)}
        prefix="$"
        autoComplete="off"
      />,
      calc ? formatCurrency(calc.netCaseCost) : "\u2014",
      calc ? formatCurrency(calc.unitCost) : "\u2014",
      formatCurrency(parseFloat(p.price)),
      calc ? formatPercent(calc.margin) : "\u2014",
      <TextField
        key={`vsku-${p.shopifyVariantId}`}
        label=""
        labelHidden
        value={row?.vendorSku || ""}
        onChange={(v) => handleEditChange(p.shopifyVariantId, "vendorSku", v)}
        autoComplete="off"
      />,
      <Checkbox
        key={`disc-${p.shopifyVariantId}`}
        label=""
        labelHidden
        checked={row?.isDiscontinued || false}
        onChange={(v) => handleEditChange(p.shopifyVariantId, "isDiscontinued", v)}
      />,
      <Checkbox
        key={`flag-${p.shopifyVariantId}`}
        label=""
        labelHidden
        checked={row?.flaggedForReview || false}
        onChange={(v) => handleEditChange(p.shopifyVariantId, "flaggedForReview", v)}
      />,
      <TextField
        key={`note-${p.shopifyVariantId}`}
        label=""
        labelHidden
        value={row?.reviewNote || ""}
        onChange={(v) => handleEditChange(p.shopifyVariantId, "reviewNote", v)}
        autoComplete="off"
        placeholder="Note..."
      />,
    ];
  });

  return (
    <>
      <Page
        title={vendor.name}
        backAction={{ content: "Vendors", url: "/" }}
        primaryAction={{
          content: "Create Purchase Order",
          url: `/vendors/${vendorId}/purchase-orders/new`,
        }}
        secondaryActions={[
          { content: "Edit Vendor", url: `/vendors/${vendorId}/edit` },
          {
            content: "Delete Vendor",
            destructive: true,
            onAction: () => setDeleteModalOpen(true),
          },
        ]}
      >
        <Layout>
          {/* Vendor Summary */}
          <Layout.Section>
            <Card>
              <BlockStack gap="400">
                <InlineStack gap="800" wrap>
                  <BlockStack gap="100">
                    <Text as="span" tone="subdued">Code</Text>
                    <Text as="span" fontWeight="semibold">{vendor.code || "\u2014"}</Text>
                  </BlockStack>
                  {vendor.accountNumber && (
                    <BlockStack gap="100">
                      <Text as="span" tone="subdued">Account #</Text>
                      <Text as="span" fontWeight="semibold">{vendor.accountNumber}</Text>
                    </BlockStack>
                  )}
                  <BlockStack gap="100">
                    <Text as="span" tone="subdued">Contact</Text>
                    <Text as="span" fontWeight="semibold">{vendor.contactName || "\u2014"}</Text>
                  </BlockStack>
                  <BlockStack gap="100">
                    <Text as="span" tone="subdued">Email</Text>
                    <Text as="span" fontWeight="semibold">{vendor.email || "\u2014"}</Text>
                  </BlockStack>
                  {vendor.orderEmail && (
                    <BlockStack gap="100">
                      <Text as="span" tone="subdued">Order Email</Text>
                      <Text as="span" fontWeight="semibold">{vendor.orderEmail}</Text>
                    </BlockStack>
                  )}
                  <BlockStack gap="100">
                    <Text as="span" tone="subdued">Phone</Text>
                    <Text as="span" fontWeight="semibold">{vendor.phone || "\u2014"}</Text>
                  </BlockStack>
                  <BlockStack gap="100">
                    <Text as="span" tone="subdued">Discount</Text>
                    <Text as="span" fontWeight="semibold">{vendor.discountPercent}%</Text>
                  </BlockStack>
                  <BlockStack gap="100">
                    <Text as="span" tone="subdued">Terms</Text>
                    <Text as="span" fontWeight="semibold">{vendor.paymentTerms || "\u2014"}</Text>
                  </BlockStack>
                  <BlockStack gap="100">
                    <Text as="span" tone="subdued">Status</Text>
                    <Badge tone={vendor.isActive ? "success" : undefined}>
                      {vendor.isActive ? "Active" : "Inactive"}
                    </Badge>
                  </BlockStack>
                </InlineStack>
                {(vendor.addressLine1 || vendor.mailingAddressLine1 || vendor.website) && (
                  <InlineStack gap="800" wrap>
                    {vendor.addressLine1 && (
                      <BlockStack gap="100">
                        <Text as="span" tone="subdued">Main Address</Text>
                        <Text as="span" fontWeight="semibold">
                          {[vendor.addressLine1, vendor.addressLine2].filter(Boolean).join(", ")}{", "}
                          {[vendor.city, vendor.province, vendor.postalCode].filter(Boolean).join(", ")}
                        </Text>
                      </BlockStack>
                    )}
                    {vendor.mailingAddressLine1 && (
                      <BlockStack gap="100">
                        <Text as="span" tone="subdued">Mailing Address</Text>
                        <Text as="span" fontWeight="semibold">
                          {[vendor.mailingAddressLine1, vendor.mailingAddressLine2].filter(Boolean).join(", ")}{", "}
                          {[vendor.mailingCity, vendor.mailingProvince, vendor.mailingPostalCode].filter(Boolean).join(", ")}
                        </Text>
                      </BlockStack>
                    )}
                    {vendor.website && (
                      <BlockStack gap="100">
                        <Text as="span" tone="subdued">Website</Text>
                        <Text as="span" fontWeight="semibold">{vendor.website}</Text>
                      </BlockStack>
                    )}
                  </InlineStack>
                )}
              </BlockStack>
            </Card>
          </Layout.Section>

          {/* Product Mapping Table */}
          <Layout.Section>
            <Card padding="0">
              <div style={{ padding: "16px" }}>
                <BlockStack gap="400">
                  <InlineStack align="space-between">
                    <Text variant="headingMd" as="h2">
                      Products ({filteredProducts.length !== products.length ? `${filteredProducts.length} of ${products.length}` : products.length})
                    </Text>
                    <InlineStack gap="200">
                      <Button onClick={handleRefresh} loading={syncing}>
                        Refresh from Shopify
                      </Button>
                      {dirtyCount > 0 && (
                        <Button
                          variant="primary"
                          onClick={handleSaveAll}
                          loading={saving}
                        >
                          {`Save ${dirtyCount} Change${dirtyCount !== 1 ? "s" : ""}`}
                        </Button>
                      )}
                    </InlineStack>
                  </InlineStack>

                  <TextField
                    label=""
                    labelHidden
                    placeholder="Search by SKU or product name..."
                    value={searchValue}
                    onChange={setSearchValue}
                    clearButton
                    onClearButtonClick={() => setSearchValue("")}
                    autoComplete="off"
                  />
                  <InlineStack gap="200" wrap>
                    <div style={{ minWidth: "140px" }}>
                      <Select
                        label="Category"
                        labelHidden
                        options={[
                          { label: "All Categories", value: "" },
                          ...CATEGORIES.slice(1),
                        ]}
                        value={categoryFilter}
                        onChange={setCategoryFilter}
                      />
                    </div>
                    {filterOptions.vendors.length > 1 && (
                      <div style={{ minWidth: "140px" }}>
                        <Select
                          label="Vendor"
                          labelHidden
                          options={[{ label: "All Vendors", value: "" }, ...filterOptions.vendors]}
                          value={vendorFilter}
                          onChange={setVendorFilter}
                        />
                      </div>
                    )}
                    {filterOptions.types.length > 0 && (
                      <div style={{ minWidth: "140px" }}>
                        <Select
                          label="Type"
                          labelHidden
                          options={[{ label: "All Types", value: "" }, ...filterOptions.types]}
                          value={typeFilter}
                          onChange={setTypeFilter}
                        />
                      </div>
                    )}
                    {filterOptions.tags.length > 0 && (
                      <div style={{ minWidth: "140px" }}>
                        <Select
                          label="Tags"
                          labelHidden
                          options={[{ label: "All Tags", value: "" }, ...filterOptions.tags]}
                          value={tagFilter}
                          onChange={setTagFilter}
                        />
                      </div>
                    )}
                    {filterOptions.option1.values.length > 0 && (
                      <div style={{ minWidth: "140px" }}>
                        <Select
                          label={filterOptions.option1.name}
                          labelHidden
                          options={[
                            { label: `All ${filterOptions.option1.name}`, value: "" },
                            ...filterOptions.option1.values,
                          ]}
                          value={option1Filter}
                          onChange={setOption1Filter}
                        />
                      </div>
                    )}
                    {filterOptions.option2.values.length > 0 && (
                      <div style={{ minWidth: "140px" }}>
                        <Select
                          label={filterOptions.option2.name}
                          labelHidden
                          options={[
                            { label: `All ${filterOptions.option2.name}`, value: "" },
                            ...filterOptions.option2.values,
                          ]}
                          value={option2Filter}
                          onChange={setOption2Filter}
                        />
                      </div>
                    )}
                    <Checkbox
                      label="Flagged only"
                      checked={showFlaggedOnly}
                      onChange={setShowFlaggedOnly}
                    />
                    {(categoryFilter || vendorFilter || typeFilter || tagFilter || option1Filter || option2Filter || showFlaggedOnly) && (
                      <Button
                        variant="plain"
                        onClick={() => {
                          setCategoryFilter("");
                          setVendorFilter("");
                          setTypeFilter("");
                          setTagFilter("");
                          setOption1Filter("");
                          setOption2Filter("");
                          setShowFlaggedOnly(false);
                        }}
                      >
                        Clear filters
                      </Button>
                    )}
                  </InlineStack>
                </BlockStack>
              </div>

              {productsError && (
                <div style={{ padding: "0 16px 16px" }}>
                  <Banner tone="critical">
                    <p>{productsError}</p>
                  </Banner>
                </div>
              )}

              {productsLoading ? (
                <div style={{ padding: "40px", textAlign: "center" }}>
                  <Spinner size="large" />
                  <br />
                  <Text as="p" tone="subdued">
                    Loading products from Shopify...
                  </Text>
                </div>
              ) : (
                <div style={{ overflowX: "auto" }}>
                  <DataTable
                    columnContentTypes={[
                      "text", "text", "numeric",
                      "text", "numeric", "numeric",
                      "numeric", "numeric", "numeric", "numeric",
                      "text", "text", "text", "text",
                    ]}
                    headings={[
                      "SKU", "Product", "QOH",
                      "Category", "Pack", "Case Cost",
                      "Net Case", "Unit Cost", "Retail", "Margin",
                      "Vendor SKU", "Disc.", "Flag", "Review Note",
                    ]}
                    rows={rows}
                    hoverable
                    increasedTableDensity
                  />
                </div>
              )}
            </Card>
          </Layout.Section>
        </Layout>

        <DeleteConfirmationModal
          open={deleteModalOpen}
          title={`Delete ${vendor.name}?`}
          message={`This will permanently delete the vendor and all ${vendor.productCount || 0} associated product mappings. This cannot be undone.`}
          onConfirm={handleDelete}
          onCancel={() => setDeleteModalOpen(false)}
        />

        {toastMessage && (
          <Toast
            content={toastMessage}
            onDismiss={() => setToastMessage(null)}
          />
        )}
      </Page>
    </>
  );
}
